<template>
  <modal
    v-if="open"
    :title="$t('Inventory.leaving_title')"
    closable
    @close-request="requestClose"
  >
    <form class="leaving-form" @submit.prevent="submit">
      <div class="leaving-form__banner">
        <i class="fa fa-sign-out" aria-hidden="true" />
        <div>
          <strong>{{ $t('Inventory.leaving_count', {count: displayOrders.length}) }}</strong>
          <span>{{ $t('Inventory.leaving_help') }}</span>
        </div>
      </div>

      <div class="leaving-form__orders">
        <div v-for="order in displayOrders" :key="orderId(order)" class="leaving-order">
          <span class="leaving-order__id">{{ orderId(order) }}</span>
          <div>
            <strong>{{ vehicleTitle(order) }}</strong>
            <span>{{ order.vin || $t('Inventory.not_provided') }}</span>
          </div>
          <small>{{ $realName(order, 'payer') || '—' }}</small>
        </div>
      </div>

      <div class="leaving-form__grid">
        <label class="form-group">
          <span>{{ $t('Inventory.dispatch_driver') }} *</span>
          <select v-model="driverUID" class="form-control" required>
            <option value="">{{ $t('Inventory.choose_driver') }}</option>
            <option v-for="driver in drivers" :key="driverId(driver)" :value="driverId(driver)">
              {{ driverName(driver) }}
            </option>
          </select>
        </label>
        <label class="form-group">
          <span>{{ $t('Inventory.date_left') }} *</span>
          <input v-model="dateLeftInput" class="form-control" type="datetime-local" required>
        </label>
      </div>

      <div class="leaving-form__grid">
        <label class="form-group">
          <span>{{ $t('Inventory.requester_name') }} *</span>
          <input
            v-model.trim="requesterName"
            class="form-control"
            type="text"
            :placeholder="$t('Inventory.requester_name_placeholder')"
            required
          >
        </label>
        <label class="form-group">
          <span>{{ $t('Inventory.requester_organization') }} *</span>
          <input
            v-model.trim="requesterOrganization"
            class="form-control"
            type="text"
            :placeholder="$t('Inventory.requester_organization_placeholder')"
            required
          >
        </label>
      </div>

      <label class="form-group">
        <span>{{ $t('Inventory.additional_request_comment') }}</span>
        <textarea
          v-model.trim="comment"
          class="form-control"
          rows="3"
          :placeholder="$t('Inventory.additional_request_comment_placeholder')"
        />
      </label>

      <div v-if="feedback" class="leaving-form__message" :class="feedbackClass" role="alert">
        {{ feedback }}
      </div>

      <div v-if="failedDispatchResults.length" class="leaving-form__retry">
        <span>
          {{ $t('Inventory.dispatch_failed_count') }}: {{ failedDispatchResults.length }}
          ({{ failedDispatchResults.map(result => result.orderId).join(', ') }}).
          {{ $t('Inventory.retry_before_closing') }}
        </span>
        <button type="button" class="btn btn-light" :disabled="submitting" @click="retryFailedDispatches">
          <i class="fa fa-refresh mr-1" aria-hidden="true" />
          {{ $t('Inventory.retry_dispatch') }}
        </button>
      </div>

      <footer class="leaving-form__footer">
        <button type="button" class="btn btn-light" :disabled="submitting || failedDispatchResults.length > 0" @click="requestClose">
          {{ $t('Inventory.cancel') }}
        </button>
        <button type="submit" class="btn btn-kne-orange" :disabled="!formValid || submitting || departureCommitted">
          <i :class="['fa', submitting ? 'fa-spinner fa-spin' : 'fa-truck']" aria-hidden="true" />
          {{ submitting ? $t('Inventory.saving_departure') : $t('Inventory.confirm_departure_dispatch') }}
        </button>
      </footer>
    </form>
  </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import { canLeaveInventory } from '../../../../inventory/inventorySelectors'
import { markInventoryLeaving, retryInventoryDispatch } from '../../../../inventory/inventoryService'
import { fromTorontoDateTimeInput, toTorontoDateTimeInput } from './inventoryDateTime'

export default {
  name: 'InventoryLeavingModal',

  components: {
    Modal
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    orders: {
      type: Array,
      default: () => []
    },
    drivers: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      driverUID: '',
      requesterName: '',
      requesterOrganization: '',
      comment: '',
      dateLeftInput: toTorontoDateTimeInput(),
      feedback: '',
      feedbackType: 'error',
      submitting: false,
      departureCommitted: false,
      updatedOrderIds: [],
      committedOrders: [],
      dispatchResults: []
    }
  },

  computed: {
    companyUID () {
      return this.$store.state.authorization && this.$store.state.authorization.company
    },
    operator () {
      const user = this.$store.state.user || {}
      const userInfo = this.$store.state.userInfo || {}
      return {
        uid: user.uid || '',
        name: userInfo.name || user.displayName || user.email || ''
      }
    },
    eligibleOrders () {
      return this.orders.filter(canLeaveInventory)
    },
    displayOrders () {
      return this.departureCommitted ? this.committedOrders : this.eligibleOrders
    },
    selectedDriver () {
      const driver = this.drivers.find(driver => this.driverId(driver) === this.driverUID)
      return driver
        ? {uid: this.driverId(driver), name: this.driverName(driver)}
        : null
    },
    formValid () {
      return Boolean(
        this.companyUID &&
        this.eligibleOrders.length &&
        this.selectedDriver &&
        this.requesterName &&
        this.requesterOrganization &&
        !isNaN(fromTorontoDateTimeInput(this.dateLeftInput))
      )
    },
    failedDispatchResults () {
      return this.dispatchResults.filter(result => !result.queued)
    },
    feedbackClass () {
      return this.feedbackType === 'success'
        ? 'leaving-form__message--success'
        : 'leaving-form__message--error'
    }
  },

  watch: {
    open (value) {
      if (value) this.reset()
    }
  },

  methods: {
    orderId (order) {
      return order['.key'] || order.orderNumber || order.key || ''
    },
    driverId (driver) {
      return String(driver.uid || driver.key || driver['.key'] || '')
    },
    driverName (driver) {
      return driver.name || driver.value || driver['.value'] || ''
    },
    vehicleTitle (order) {
      return [order.year, order.make, order.model, order.trim].filter(Boolean).join(' ') || this.$t('Inventory.vehicle_pending')
    },
    defaultRequesterOrganization () {
      const payerNames = this.eligibleOrders
        .map(order => order.payerName)
        .filter(Boolean)
        .filter((name, index, values) => values.indexOf(name) === index)
      return payerNames.length === 1 ? payerNames[0] : ''
    },
    async submit () {
      if (!this.formValid || this.submitting || this.departureCommitted) return
      this.submitting = true
      this.feedback = ''
      try {
        this.committedOrders = this.eligibleOrders.slice()
        const result = await markInventoryLeaving({
          companyUID: this.companyUID,
          orderIds: this.eligibleOrders.map(this.orderId),
          dateLeft: fromTorontoDateTimeInput(this.dateLeftInput),
          driver: this.selectedDriver,
          requesterName: this.requesterName,
          requesterOrganization: this.requesterOrganization,
          comment: this.comment,
          operator: this.operator
        })
        this.departureCommitted = true
        this.updatedOrderIds = result.updatedOrderIds || []
        this.dispatchResults = result.dispatchResults || []
        if (result.allDispatchesQueued !== false) {
          this.feedback = this.$t('Inventory.departure_success')
          this.feedbackType = 'success'
          this.$emit('completed', result)
          this.$emit('close')
        } else {
          this.feedback = this.$t('Inventory.departure_dispatch_partial_error')
          this.feedbackType = 'error'
          this.$emit('partial', result)
        }
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.departure_error')
        this.feedbackType = 'error'
      } finally {
        this.submitting = false
      }
    },
    async retryFailedDispatches () {
      if (!this.failedDispatchResults.length || !this.selectedDriver) return
      this.submitting = true
      try {
        const retried = await Promise.all(this.failedDispatchResults.map((result, index) => {
          return retryInventoryDispatch({
            companyUID: this.companyUID,
            orderId: result.orderId,
            driver: this.selectedDriver,
            deliveryOrder: (result.payload && result.payload.deliveryOrder) || index + 1,
            comment: result.payload && result.payload.comment !== undefined
              ? result.payload.comment
              : this.comment
          })
        }))
        const retriedById = retried.reduce((results, item) => {
          results[item.orderId] = item
          return results
        }, {})
        this.dispatchResults = this.dispatchResults.map(result => retriedById[result.orderId] || result)
        if (!this.failedDispatchResults.length) {
          this.feedback = this.$t('Inventory.dispatch_retry_success')
          this.feedbackType = 'success'
          this.$emit('completed', {
            updatedOrderIds: this.updatedOrderIds,
            dispatchResults: this.dispatchResults,
            allDispatchesQueued: true
          })
          this.$emit('close')
        } else {
          this.feedback = this.$t('Inventory.dispatch_retry_error')
          this.feedbackType = 'error'
        }
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.dispatch_retry_error')
        this.feedbackType = 'error'
      } finally {
        this.submitting = false
      }
    },
    requestClose () {
      if (!this.submitting && !this.failedDispatchResults.length) this.$emit('close')
    },
    reset () {
      this.driverUID = ''
      this.requesterName = ''
      this.comment = ''
      this.dateLeftInput = toTorontoDateTimeInput()
      this.feedback = ''
      this.feedbackType = 'error'
      this.submitting = false
      this.departureCommitted = false
      this.updatedOrderIds = []
      this.committedOrders = []
      this.dispatchResults = []
      this.$nextTick(() => {
        this.requesterOrganization = this.defaultRequesterOrganization()
      })
    }
  }
}
</script>

<style scoped>
.leaving-form {
  width: min(760px, 76vw);
  max-width: 100%;
  padding: 20px 24px 24px;
  color: #354252;
}

.leaving-form__banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 17px;
  margin-bottom: 18px;
  border-radius: 11px;
  color: #71490a;
  background: #fff1cd;
}

.leaving-form__banner > i {
  font-size: 1.5rem;
}

.leaving-form__banner div {
  display: flex;
  flex-direction: column;
}

.leaving-form__banner span {
  font-size: .83rem;
}

.leaving-form__orders {
  display: flex;
  max-height: 220px;
  flex-direction: column;
  gap: 7px;
  overflow-y: auto;
  padding-right: 3px;
  margin-bottom: 20px;
}

.leaving-order {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border: 1px solid #e5e9ed;
  border-radius: 9px;
}

.leaving-order__id {
  padding: 5px 8px;
  border-radius: 6px;
  color: #985912;
  background: #fff0dd;
  font-size: .76rem;
  font-weight: 800;
}

.leaving-order div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.leaving-order div span {
  overflow-wrap: anywhere;
  color: #74808d;
  font-family: monospace;
  font-size: .78rem;
}

.leaving-order small {
  overflow: hidden;
  max-width: 180px;
  color: #6e7b88;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leaving-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.leaving-form label > span {
  display: block;
  margin-bottom: 6px;
  color: #52606e;
  font-size: .82rem;
  font-weight: 700;
}

.leaving-form__message,
.leaving-form__retry {
  padding: 12px 14px;
  margin: 12px 0;
  border-radius: 8px;
}

.leaving-form__message--error {
  color: #8d2f32;
  background: #fde7e7;
}

.leaving-form__message--success {
  color: #17613a;
  background: #e2f6e9;
}

.leaving-form__retry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #f0c1c1;
  color: #812e32;
  background: #fff8f8;
}

.leaving-form__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid #e7ebef;
}

@media (max-width: 600px) {
  .leaving-form {
    width: 100%;
    padding: 16px;
  }

  .leaving-form__grid {
    grid-template-columns: 1fr;
  }

  .leaving-order {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .leaving-order small {
    display: none;
  }

  .leaving-form__footer {
    flex-direction: column-reverse;
  }

  .leaving-form__footer .btn {
    width: 100%;
  }
}
</style>
